import { trackLocationSelected } from './literbokse-tracking.js';

class LocationSelector extends HTMLElement {
  connectedCallback() {
    this._dialog = this.querySelector('dialog');
    const settings = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
    this._currentLocation = settings.currentLocation;
    this._cartItemCount = settings.cartItemCount;
    this._requireLocation = settings.requireLocation;
    this._warningEl = this._dialog.querySelector('.location-selector__warning');
    this._listEl = this._dialog.querySelector('.location-selector__list');
    this._confirmEl = this._dialog.querySelector('.location-selector__confirm');
    this._confirmList = this._dialog.querySelector('.location-selector__confirm-list');

    this._backdrop = this.querySelector('.location-selector__backdrop');
    this._closeBtn = this._dialog.querySelector('.location-selector__close');
    this._closeBtn.addEventListener('click', () => this._close());
    this._backdrop.addEventListener('click', () => this._close());

    for (const item of this._dialog.querySelectorAll('.location-selector__item')) {
      item.addEventListener('click', () => this._selectLocation(item.dataset.locationName));
    }

    document.querySelectorAll('.location-pill__btn').forEach(btn => {
      btn.addEventListener('click', () => this._open());
    });

    this._dialog.querySelector('.location-selector__confirm-cancel').addEventListener('click', () => this._cancelConfirmation());
    this._dialog.querySelector('.location-selector__confirm-accept').addEventListener('click', () => this._acceptConfirmation());

    this._validLocations = new Map(
      [...this._dialog.querySelectorAll('.location-selector__item')].map(el => [el.dataset.locationName.toLowerCase(), el.dataset.locationName])
    );

    this._highlightCurrent();

    const urlLocationParam = new URLSearchParams(window.location.search).get('location');
    const urlLocation = urlLocationParam ? this._validLocations.get(urlLocationParam.toLowerCase()) : null;
    if (urlLocation) {
      if (urlLocation !== this._currentLocation) {
        if (this._cartItemCount === 0) {
          this._applyLocation(urlLocation);
        } else {
          this._open().then(() => this._selectLocation(urlLocation));
        }
        return;
      }
      this._cleanLocationParam();
    }

    if (!Shopify.designMode && !this._currentLocation && settings.requireLocation) {
      this._open();
    }
  }

  _highlightCurrent() {
    for (const item of this._dialog.querySelectorAll('.location-selector__item')) {
      item.classList.toggle('location-selector__item--active', this._currentLocation && item.dataset.locationName === this._currentLocation);
    }
  }

  async _open() {
    this._confirmEl.hidden = true;
    this._listEl.hidden = false;

    try {
      const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      this._cartItemCount = cart.item_count;
    } catch {}

    this._warningEl.hidden = this._cartItemCount === 0;
    const canDismiss = this._currentLocation || !this._requireLocation;
    this._closeBtn.hidden = !canDismiss;
    this._backdrop.style.pointerEvents = canDismiss ? '' : 'none';
    this._dialog.show();
  }

  _close() {
    this._dialog.close();
  }

  async _selectLocation(name) {
    if (name === this._currentLocation || this._cartItemCount === 0) {
      return this._applyLocation(name);
    }

    try {
      this._setLoading(true);
      const unavailable = await this._getUnavailableItems(name);

      if (unavailable.length === 0) {
        return this._applyLocation(name);
      }

      this._showConfirmation(name, unavailable);
    } catch {
      this._setLoading(false);
      return;
    } finally {
      this._setLoading(false);
    }
  }

  async _getUnavailableItems(targetLocation) {
    const res = await fetch(`${window.Shopify.routes.root}?section_id=cart-location-check`);
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const jsonEl = doc.querySelector('[data-cart-location-data]');
    const data = JSON.parse(jsonEl.textContent);

    return data.filter(item => {
      if (item.locations.length > 0 && !item.locations.includes(targetLocation)) return true;

      if (item.components && item.components.length > 0) {
        return item.components.some(c => c.locations.length > 0 && !c.locations.includes(targetLocation));
      }

      return false;
    });
  }

  _showConfirmation(targetLocation, unavailableItems) {
    this._pendingLocation = targetLocation;
    this._pendingRemovals = unavailableItems.map(item => item.key);

    this._listEl.hidden = true;
    this._warningEl.hidden = true;

    this._confirmList.innerHTML = '';
    for (const item of unavailableItems) {
      const unavailableComponents = item.components
        ? item.components.filter(c => c.locations.length > 0 && !c.locations.includes(targetLocation))
        : [];

      if (unavailableComponents.length > 0) {
        for (const component of unavailableComponents) {
          const li = document.createElement('li');
          li.className = 'location-selector__confirm-item';
          li.textContent = component.title;
          this._confirmList.appendChild(li);
        }
      } else {
        const li = document.createElement('li');
        li.className = 'location-selector__confirm-item';
        li.textContent = item.title;
        this._confirmList.appendChild(li);
      }
    }

    this._confirmEl.hidden = false;
  }

  _cancelConfirmation() {
    this._confirmEl.hidden = true;
    this._listEl.hidden = false;
    this._warningEl.hidden = this._cartItemCount === 0;
    this._pendingLocation = null;
    this._pendingRemovals = null;
  }

  async _acceptConfirmation() {
    this._setLoading(true);

    for (const key of this._pendingRemovals) {
      await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      });
    }

    await this._applyLocation(this._pendingLocation);
  }

  async _applyLocation(name) {
    trackLocationSelected(name);
    await this._setCartAttribute(name);
    this._cleanLocationParam();
    window.location.reload();
  }

  _cleanLocationParam() {
    const url = new URL(window.location);
    if (!url.searchParams.has('location')) return;
    url.searchParams.delete('location');
    history.replaceState({}, '', url);
  }

  async _setCartAttribute(name) {
    await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { location: name } })
    });
  }

  _setLoading(loading) {
    for (const item of this._dialog.querySelectorAll('.location-selector__item')) {
      item.disabled = loading;
    }
    const acceptBtn = this._dialog.querySelector('.location-selector__confirm-accept');
    if (acceptBtn) acceptBtn.disabled = loading;
  }
}

customElements.define('location-selector', LocationSelector);
